import React, { useMemo, useState } from 'react';
import { Alert, Box, Stack, Tooltip, Typography } from '@mui/material';
import { RotationClip, RotationItem } from '../../services/api';
import {
  STATE_COLOR, STATE_LABEL, TEST_WINDOW_DAYS, canMove, rotationStart, sendReschedule,
} from './ShortsRotationPanel';

const DAY_MS = 86_400_000;

export type CalendarEventKind = 'publish' | 'thumbnail' | 'title' | 'end';

export interface CalendarEvent {
  id: string;
  clipId: string;
  kind: CalendarEventKind;
  at: Date;
  label: string;
  item?: RotationItem;
  /** The thumbnail rank or title idx a drag moves. */
  key?: number;
  start?: Date;
}

/** Local midnight, so events land on the day the viewer sees. */
export const dayOf = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** Whole calendar days between two dates (DST-safe: rounds the hour drift). */
export const daysBetween = (a: Date, b: Date): number =>
  Math.round((dayOf(b).getTime() - dayOf(a).getTime()) / DAY_MS);

/** Every dated thing in the rotations, as calendar events (pure). */
export const buildEvents = (clips: RotationClip[], labels: Record<string, string>):
  CalendarEvent[] => {
  const out: CalendarEvent[] = [];
  for (const c of clips) {
    const startIso = rotationStart(c);
    if (!startIso) continue;
    const start = new Date(startIso);
    const name = labels[c.clip_id] || c.title || c.clip_id;
    out.push({ id: `${c.clip_id}:publish`, clipId: c.clip_id, kind: 'publish', at: start,
               label: `${name} · ${c.video_id ? 'published' : 'publishes'}`, start,
               item: c.thumbnails.items.find((i) => i.after_days != null && i.after_days <= 0) });
    for (const it of c.thumbnails.items) {
      if (it.after_days == null || it.after_days <= 0 || !it.due_at) continue;
      out.push({ id: `${c.clip_id}:t${it.rank}`, clipId: c.clip_id, kind: 'thumbnail',
                 at: new Date(it.due_at), item: it, key: it.rank, start,
                 label: `${name} · #${it.rank}${it.person ? ` ${it.person}` : ''}` });
    }
    for (const it of c.titles?.items ?? []) {
      if (it.after_days == null || it.after_days <= 0 || !it.due_at) continue;
      out.push({ id: `${c.clip_id}:l${it.idx}`, clipId: c.clip_id, kind: 'title',
                 at: new Date(it.due_at), item: it, key: it.idx, start,
                 label: `${name} · title “${it.title}”` });
    }
    if (c.thumbnails.end_at) {
      const settled = c.thumbnails.settled;
      out.push({ id: `${c.clip_id}:end`, clipId: c.clip_id, kind: 'end',
                 at: new Date(c.thumbnails.end_at), start,
                 label: settled != null ? `${name} · settled on #${settled}`
                   : `${name} · settles (keeps the ${c.settle})` });
    }
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
};

/** Weeks (Sunday first) covering every event, at least through today. */
export const weeksFor = (events: CalendarEvent[]): Date[][] => {
  if (!events.length) return [];
  const first = dayOf(events[0].at);
  const last = dayOf(events[events.length - 1].at);
  const from = new Date(first);
  from.setDate(from.getDate() - from.getDay());
  const weeks: Date[][] = [];
  const cur = new Date(from);
  while (cur <= last || weeks.length === 0) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

const KIND_BG: Record<CalendarEventKind, string> = {
  publish: 'primary.main', thumbnail: 'transparent', title: 'transparent', end: 'transparent',
};

const EventChip: React.FC<{
  ev: CalendarEvent; onOpen: (clipId: string) => void;
  onDragStart: (ev: CalendarEvent) => void;
  onDragEnd: () => void;
}> = ({ ev, onOpen, onDragStart, onDragEnd }) => {
  const it = ev.item;
  const draggable = ev.kind !== 'publish' && ev.kind !== 'end' && !!it && canMove(it);
  const color = it && ev.kind !== 'publish' ? STATE_COLOR[it.state] : 'default';
  const time = ev.at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const tip = [ev.label, time, it && ev.kind !== 'publish' ? STATE_LABEL[it.state] : null,
               draggable ? 'drag to another day to move it' : null,
               it?.attempts.filter((a) => a.error).slice(-1)[0]?.error
                 ? `last error: ${it.attempts.filter((a) => a.error).slice(-1)[0].error}` : null]
    .filter(Boolean).join(' · ');
  return (
    <Tooltip title={tip}>
      <Stack direction="row" spacing={0.5} alignItems="center"
             draggable={draggable}
             onDragStart={(e) => {
               e.dataTransfer.setData('text/plain', ev.id);
               e.dataTransfer.effectAllowed = 'move';
               onDragStart(ev);
             }}
             onDragEnd={onDragEnd}
             onClick={() => onOpen(ev.clipId)}
             data-testid={`cal-event-${ev.id}`}
             sx={{
               cursor: draggable ? 'grab' : 'pointer', borderRadius: 0.5, px: 0.25,
               border: '1px solid',
               borderColor: color === 'default' ? 'divider' : `${color}.main`,
               bgcolor: ev.kind === 'publish' ? 'action.selected' : KIND_BG[ev.kind],
               opacity: it && ['skipped', 'cut_off', 'shown'].includes(it.state)
                 && ev.kind !== 'publish' ? 0.55 : 1,
               fontStyle: ev.kind === 'end' ? 'italic' : 'normal',
             }}>
        {it?.image_url && ev.kind !== 'title' && (
          <Box component="img" src={it.image_url} alt=""
               sx={{ width: 12, height: 20, objectFit: 'cover', flex: '0 0 auto' }} />
        )}
        <Typography variant="caption" noWrap sx={{ fontSize: 10, lineHeight: 1.4 }}>
          {ev.label}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

interface Props {
  clips: RotationClip[];
  /** Short names per clip id, e.g. "3" for the third Short. */
  labels?: Record<string, string>;
  onOpen: (clipId: string) => void;
  onChanged: () => void;
}

/**
 * Every Short's publish, thumbnail and title swaps, and settle day on one
 * calendar. A swap still ahead can be dragged to another day, which moves
 * its slot exactly like the day picker in the list view.
 */
const RotationCalendar: React.FC<Props> = ({ clips, labels = {}, onOpen, onChanged }) => {
  const events = useMemo(() => buildEvents(clips, labels), [clips, labels]);
  const weeks = useMemo(() => weeksFor(events), [events]);
  const byDay = useMemo(() => {
    const m: Record<string, CalendarEvent[]> = {};
    for (const ev of events) (m[dayKey(ev.at)] ||= []).push(ev);
    return m;
  }, [events]);
  const [dragging, setDragging] = useState<CalendarEvent | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const today = dayKey(new Date());

  const drop = async (day: Date) => {
    const ev = dragging;
    setDragging(null);
    setOver(null);
    if (!ev || !ev.start || ev.key == null) return;
    const n = daysBetween(ev.start, day);
    if (n === ev.item?.after_days) return;
    if (n < 1 || n > TEST_WINDOW_DAYS) {
      setMessage({ ok: false, text: `A slot must be 1 to ${TEST_WINDOW_DAYS} days after `
        + `publish; that day is day ${n}.` });
      return;
    }
    const body = ev.kind === 'title'
      ? { titles: [{ idx: ev.key, after_days: n }] }
      : { thumbnails: [{ rank: ev.key, after_days: n }] };
    setMessage({ ok: true, text: `Moving to day ${n}…` });
    setMessage(await sendReschedule(ev.clipId, body));
    onChanged();
  };

  if (!events.length) {
    return <Alert severity="info">Nothing scheduled yet.</Alert>;
  }
  return (
    <Stack spacing={1}>
      {message && (
        <Alert severity={message.ok ? 'success' : 'error'} onClose={() => setMessage(null)}
               sx={{ py: 0 }}>
          {message.text}
        </Alert>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                 gap: 0.5, overflowX: 'auto' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <Typography key={d} variant="caption" color="text.secondary" align="center">
            {d}
          </Typography>
        ))}
        {weeks.flat().map((day) => {
          const k = dayKey(day);
          const evs = byDay[k] ?? [];
          // Dropping is only offered on days a slot may land: 1..8 after publish.
          const n = dragging?.start ? daysBetween(dragging.start, day) : null;
          const droppable = n != null && n >= 1 && n <= TEST_WINDOW_DAYS;
          return (
            <Box key={k} data-testid={`cal-day-${k}`}
                 onDragOver={(e) => { if (droppable) { e.preventDefault(); setOver(k); } }}
                 onDragLeave={() => setOver((o) => (o === k ? null : o))}
                 onDrop={(e) => { e.preventDefault(); drop(day); }}
                 sx={{
                   minHeight: 90, p: 0.5, borderRadius: 1, border: '1px solid',
                   borderColor: k === today ? 'primary.main' : 'divider',
                   bgcolor: over === k ? 'action.selected'
                     : dragging && droppable ? 'action.hover' : 'background.paper',
                 }}>
              <Typography variant="caption" color={k === today ? 'primary' : 'text.secondary'}
                          sx={{ fontWeight: k === today ? 700 : 400 }}>
                {day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                {n != null && dragging && droppable ? ` · day ${n}` : ''}
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                {evs.map((ev) => (
                  <EventChip key={ev.id} ev={ev} onOpen={onOpen}
                             onDragStart={(e) => setDragging(e)}
                             onDragEnd={() => { setDragging(null); setOver(null); }} />
                ))}
              </Stack>
            </Box>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Click an event to open that Short&apos;s thumbnails. Drag a swap that hasn&apos;t
        happened yet to another day to move it (1 to {TEST_WINDOW_DAYS} days after
        publish). Outline colour is the same as the list: green live, blue swapping,
        red failed.
      </Typography>
    </Stack>
  );
};

export default RotationCalendar;
